import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/components/appointmentRequest/appointmentRequestTable.module.css";
import DropDown from "@/components/forms/inputs/dropDown";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { api } from "@/helper/api";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import {
  AppointmentRequestCompanySummary,
  AppointmentRequestDepartmentGroup,
  getFlatAppointmentRequestDepartmentOptions,
  groupAppointmentRequestDepartments,
} from "@/components/appointmentRequest/appointmentRequestDepartmentOptions";

interface EditSubjectDepartmentModalProps {
  modalOpen: boolean;
  subdomain: string;
  companySubjects: AppointmentRequestCompanySummary[];
  appointmentRequestId: string;
  title: string;
  handleSetError: (error: any) => void;
  handleSetModalState: (value: boolean) => void;
  handleShowNotification: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const tabLabels = ["add", "remove"];

  return (
    <div
      role="tabpanel"
      hidden={value !== tabLabels[index]}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === tabLabels[index] && (
        <Box className={styles.tabPanel}>{children}</Box>
      )}
    </div>
  );
}

const formatCompanyNames = (companyNames: string[], conjunction: string) => {
  if (companyNames.length === 0) {
    return "0";
  }

  if (companyNames.length === 1) {
    return companyNames[0];
  }

  return `${companyNames.slice(0, -1).join(", ")} ${conjunction} ${
    companyNames[companyNames.length - 1]
  }`;
};

const EditSubjectDepartmentModal: FC<EditSubjectDepartmentModalProps> = ({
  modalOpen,
  subdomain,
  companySubjects,
  appointmentRequestId,
  title,
  handleSetError,
  handleSetModalState,
  handleShowNotification,
}) => {
  const { t } = useTranslation();

  const [tabValue, setTabValue] = useState<"add" | "remove">("add");
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<any>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<any>([]);
  const [removeDepartmentIds, setRemoveDepartmentIds] = useState<any>([]);
  const [removeSubjectIds, setRemoveSubjectIds] = useState<any>([]);
  const companySubjectIds = companySubjects.map((company) => company.id);
  const selectorsDisabled = companySubjectIds.length === 0;

  const formatGroupLabel = (group: AppointmentRequestDepartmentGroup) => (
    <div className={styles.groupOptionsWrapper}>
      <span className={styles.groupOptionsLabel}>{group.label}</span>
      <span>{group.options.length}</span>
    </div>
  );

  const companyDisplayValue =
    companySubjects.length > 5
      ? companySubjects.length.toString()
      : formatCompanyNames(
          companySubjects.map((company) => company.name),
          t("and"),
        );

  // Reset state when modal opens
  useEffect(() => {
    if (modalOpen) {
      setTabValue("add");
      setSelectedDepartmentIds([]);
      setSelectedSubjects([]);
      setRemoveDepartmentIds([]);
      setRemoveSubjectIds([]);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (companySubjects.length === 0) {
      setDepartmentOptions([]);
      return;
    }

    const additionalUrlParameters = {
      company_subject_id__in: companySubjectIds,
    };
    api.genericApiRequest({
      entity: "department",
      method: "get",
      getAllPages: true,
      successHandler: (res: any) => {
        const departments = res.data.results ?? [];
        setDepartmentOptions(
          companySubjects.length > 1
            ? groupAppointmentRequestDepartments(departments)
            : getFlatAppointmentRequestDepartmentOptions(departments),
        );
      },
      failHandler: (error: any) => {
        handleSetError(error);
      },
      parametersToRender: {
        depth: "0",
        additionalUrlParameters,
      },
    });
  }, [companySubjects]);

  const saveDropDownOnChangeData = (saveMethod: any, data: any) => {
    if (data) {
      if (typeof data === "object" && data.value) {
        return saveMethod(data.value);
      }

      if (Array.isArray(data) && data.length > 0) {
        const tempList = data.map((item) => item.value);
        return saveMethod(tempList);
      } else saveMethod([]);
    }
  };

  const handleDepartmentChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setSelectedDepartmentIds, selectedOption);
  };

  const handleSubjectSetChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setSelectedSubjects, selectedOption);
  };

  const handleRemoveDepartmentChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setRemoveDepartmentIds, selectedOption);
  };

  const handleRemoveSubjectSetChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setRemoveSubjectIds, selectedOption);
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: "add" | "remove",
  ) => {
    setTabValue(newValue);
  };

  const submitAdditionalSubjectsAndDepartments = () => {
    const submitData = {
      department_set: selectedDepartmentIds,
      person_subject_set: selectedSubjects,
    };

    api.genericApiRequest({
      method: "put",
      entity: "appointmentRequest",
      entityId: appointmentRequestId,
      submitData: submitData,
      additionalRouteParts: { add_to_existing: "/" },
      successHandler: () => {
        handleSetModalState(false);
        handleShowNotification();
      },
      failHandler: (error: any) => {
        handleSetError(error);
      },
    });
  };

  const submitRemoveSubjectsAndDepartments = () => {
    const submitData = {
      department_set: removeDepartmentIds,
      person_subject_set: removeSubjectIds,
    };

    api.genericApiRequest({
      method: "put",
      entity: "appointmentRequest",
      entityId: appointmentRequestId,
      submitData: submitData,
      additionalRouteParts: { remove_from_existing: "/" },
      successHandler: () => {
        handleSetModalState(false);
        handleShowNotification();
      },
      failHandler: (error: any) => {
        handleSetError(error);
      },
    });
  };

  const addTabContent = (
    <Grid container className={styles.container} spacing={2}>
      <Grid item xs={12}>
        <FormFieldLabel
          label={t("single_customer")}
          htmlFor="add-appointment-request-subject"
        />
        <DropDownSubjectSearch
          name="add-appointment-request-subject"
          subdomain={subdomain}
          options={[]}
          onChange={handleSubjectSetChange}
          queryParameter="users"
          subjectIdForFiltering={companySubjectIds}
          isMulti={true}
          isClearable={true}
          disabled={selectorsDisabled}
        />
      </Grid>
      <Grid item xs={12}>
        <DropDown
          label={t("department")}
          name={t("department")}
          options={departmentOptions}
          onChange={handleDepartmentChange}
          isMulti={true}
          disabled={selectorsDisabled}
          formatGroupLabel={formatGroupLabel}
        />
      </Grid>
    </Grid>
  );

  const removeTabContent = (
    <Grid container className={styles.container} spacing={2}>
      <Grid item xs={12}>
        <FormFieldLabel
          label={t("single_customer")}
          htmlFor="remove-appointment-request-subject"
        />
        <DropDownSubjectSearch
          name="remove-appointment-request-subject"
          subdomain={subdomain}
          options={[]}
          onChange={handleRemoveSubjectSetChange}
          queryParameter="users"
          subjectIdForFiltering={companySubjectIds}
          isMulti={true}
          isClearable={true}
          disabled={selectorsDisabled}
        />
      </Grid>
      <Grid item xs={12}>
        <DropDown
          label={t("department")}
          name={t("department")}
          options={departmentOptions}
          onChange={handleRemoveDepartmentChange}
          isMulti={true}
          disabled={selectorsDisabled}
          formatGroupLabel={formatGroupLabel}
        />
      </Grid>
    </Grid>
  );

  const cancelClose = useModalDismiss(modalOpen, () =>
    handleSetModalState(false),
  );

  return (
    <>
      <Dialog
        open={modalOpen}
        {...cancelClose}
        className={styles.dialogContainer}
        PaperProps={{ className: styles.dialogPaper }}
        sx={{
          "& .MuiPaper-root": {
            width: "100%",
          },
        }}
      >
        <DialogTitle textAlign="center">
          {t("edit_subject_and_or_department")}
          <Box className={styles.dialogMetadata}>
            <Typography component="div" color="textSecondary">
              <span className={styles.dialogMetadataLabel}>
                {t("appointmentrequest_involved_companies", {
                  count: companySubjects.length,
                })}
                :
              </span>{" "}
              {companyDisplayValue}
            </Typography>
            <Typography component="div" color="textSecondary">
              <span className={styles.dialogMetadataLabel}>
                {t("appointmentrequest_name")}:
              </span>{" "}
              {title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <Box className={styles.tabsContainer}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="edit tabs"
            >
              <Tab value="add" label={t("add")} />
              <Tab value="remove" label={t("remove")} />
            </Tabs>
          </Box>
          <TabPanel value={tabValue} index={0}>
            {addTabContent}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {removeTabContent}
          </TabPanel>
        </DialogContent>
        <DialogActions className={styles.buttonWrapper}>
          <GenericButton
            variant="outlined"
            onClick={() => handleSetModalState(false)}
          >
            {t("cancel")}
          </GenericButton>
          <GenericButton
            onClick={
              tabValue === "add"
                ? submitAdditionalSubjectsAndDepartments
                : submitRemoveSubjectsAndDepartments
            }
          >
            {`${tabValue === "add" ? t("add") : t("remove")} ${t("and")} ${t(
              "save",
            )}`}
          </GenericButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditSubjectDepartmentModal;
